import { GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import * as analytics from '../../../shared/analytics/analytics.json';
import {
  deleteOrIndexGroup,
  getGroupIndexLogsForDomain,
  indexAllGroups,
  indexAllGroupsBigQuery,
  indexGroupBigQuery,
  UserKeys,
} from '../../../api/users-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { emailRegex } from '../../../utils/regular-expressions';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import ChangeLogDialog from '../../../shared/change-log-dialog';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Groups from '@mui/icons-material/Groups';
import Refresh from '@mui/icons-material/Refresh';
import SharedTabHeader from '../../../shared/shared-tab-header';

export default function GroupsIndexingGrid() {
  const {
    administrationRoles,
    primaryDomain,
    currentTier,
    features,
    changeLogRoles,
  } = useUserSettings();

  const [groupEmail, setGroupEmail] = useState('');
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar, groupIndexingIsDisabled } = useStore();
  const [hasError, setHasError] = useState(false);
  const [radioValue, setRadioValue] = useState('Group');
  const [fakeLoading, setFakeLoading] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const {
    data: indexGroupLogs,
    isLoading: isIndexGroupLogsLoading,
    isFetching: isIndexGroupLogsFetching,
  } = useQuery({
    queryKey: [UserKeys.IndexGroupLog, primaryDomain],
    queryFn: () => getGroupIndexLogsForDomain(primaryDomain),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group index logs'),
    },
    enabled:
      currentTier !== 'enterprise' ||
      (currentTier === 'enterprise' &&
        !isFeatureEnabled('indexGroupsReact', features)),
  });

  const mostRecentRunTime = indexGroupLogs?.reduce((latest, log) => {
    const logTime = new Date(log.lastRunTime);
    return logTime > latest ? logTime : latest;
  }, new Date(0));

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      currentTier === 'enterprise' &&
      isFeatureEnabled('indexGroupsReact', features)
        ? indexAllGroupsBigQuery(changeLogMessage)
        : indexAllGroups(changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Indexing for groups is now running.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue indexing the groups. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.indexing.index_all_groups, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.indexing,
      });
    },
  });

  const { mutate: groupMutate, isPending: isGroupPending } = useMutation({
    mutationFn: () =>
      currentTier === 'enterprise' &&
      isFeatureEnabled('indexGroupsReact', features)
        ? indexGroupBigQuery(groupEmail, changeLogMessage)
        : deleteOrIndexGroup(groupEmail, changeLogMessage),
    onSuccess: (returnInfo) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          returnInfo === 'index'
            ? 'Group has been indexed.'
            : 'Group has been deleted from indexing.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue indexing the group. Please refresh and try again.',
      });
    },
    onSettled: () => {
      setGroupEmail('');
      createEvent(analytics.administration.indexing.index_group, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.indexing,
      });
    },
  });

  useEffect(() => {
    if (fakeLoading) {
      setTimeout(() => {
        setFakeLoading(false);
      }, 3000);
    }
  }, [fakeLoading]);

  useEffect(() => {
    if (!groupIndexingIsDisabled) {
      setFakeLoading(false);
    }
  }, [groupIndexingIsDisabled]);

  function handleDomainIndexing() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RunGroupIndexing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      setFakeLoading(true);
      mutate();
    }
  }

  function handleGroupIndexing() {
    if (!emailRegex.test(groupEmail)) {
      setHasError(true);
      return;
    }
    setHasError(false);
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RunGroupIndexing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      groupMutate();
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'lastRunTime',
        headerName: 'Run Time',
        width: 180,
        type: 'dateTime',
        valueGetter: (params: number) => {
          return new Date(params);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'manual',
        headerName: 'Manual Run',
        type: 'boolean',
        flex: 1,
      },
      {
        field: 'emailDomain',
        headerName: 'Email Domain',
        flex: 1,
      },
    ],
    []
  );

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn noFilter noQuickFilter>
        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          Last Run Time:{' '}
          {mostRecentRunTime
            ? mostRecentRunTime.toLocaleString()
            : 'No runs yet'}
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.IndexGroupLog],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </SharedToolbar>
    );
  }

  const disabledByRole = !isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.RunGroupIndexing.value
  );

  const showGroupIndexingLogsGrid =
    !isFeatureEnabled('indexGroupsReact', features) && radioValue === 'Domain';

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Groups" />
      <Box sx={{ px: 1 }}>
        <Stack gap={1} mb={2}>
          <Typography>
            {radioValue === 'Group'
              ? 'Running an index on an individual group will add the group if they do not exist in gPanel, remove the group from gPanel if they are no longer in the domain, and update the group in gPanel if they have changed.'
              : 'Running an index on a domain will remove all groups from search and groups grid for that domain within gPanel and re-add them for the entire domain. There will be a period of time where groups will not show up in search results or groups grid while the index is running.'}
          </Typography>

          <Tooltip
            arrow
            title={
              disabledByRole
                ? `You need the permission "${AdministrationRoles.RunGroupIndexing.name}" to perform this action.`
                : ''
            }
          >
            <Stack flexDirection="row" alignItems="flex-end">
              <FormControl sx={{ mt: 1 }} disabled={disabledByRole}>
                <FormLabel>
                  Select the type of indexing you would like to perform:
                </FormLabel>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-label"
                  defaultValue="Group"
                  name="radio-buttons-group"
                  value={radioValue}
                  onChange={(e) => {
                    setRadioValue(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value="Group"
                    control={<Radio />}
                    label="Individual Group"
                  />
                  <FormControlLabel
                    value="Domain"
                    control={<Radio />}
                    label="All Groups in Domain"
                  />
                </RadioGroup>
              </FormControl>

              <Stack gap={1} ml={2}>
                {radioValue === 'Group' && (
                  <Stack flexDirection="row" gap={1}>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      sx={{ width: 400 }}
                      label="Group email"
                      value={groupEmail}
                      onChange={(event) => {
                        setGroupEmail(event.target.value);
                        setHasError(false);
                      }}
                      error={hasError}
                      helperText={
                        hasError ? 'Must be a valid email address.' : ''
                      }
                      disabled={disabledByRole}
                      autoComplete="off"
                    />
                    <Button
                      variant="contained"
                      loading={isGroupPending}
                      disabled={
                        disabledByRole ||
                        (radioValue === 'Group' && !groupEmail) ||
                        isGroupPending
                      }
                      onClick={handleGroupIndexing}
                      sx={{ height: 40 }}
                    >
                      Index
                    </Button>
                  </Stack>
                )}

                {radioValue === 'Group' && <Box height={hasError ? 20 : 40} />}

                {radioValue === 'Domain' && (
                  <Button
                    variant="contained"
                    loading={groupIndexingIsDisabled || fakeLoading}
                    disabled={
                      disabledByRole || groupIndexingIsDisabled || fakeLoading
                    }
                    onClick={handleDomainIndexing}
                    sx={{ height: 40 }}
                  >
                    Index
                  </Button>
                )}
              </Stack>
            </Stack>
          </Tooltip>
        </Stack>

        {showGroupIndexingLogsGrid && (
          <Box sx={{ height: '50vh', mx: 'auto' }}>
            <SharedDataGridClient
              columns={columns}
              rows={indexGroupLogs || []}
              slots={{
                toolbar: CustomToolbar,
                ...createGridOverlays('Group Indexing Logs', <Groups />),
              }}
              getRowId={(row) => row.lastRunTime}
              loading={isIndexGroupLogsLoading || isIndexGroupLogsFetching}
            />
          </Box>
        )}
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (radioValue === 'Domain') {
            setFakeLoading(true);
            mutate();
          } else {
            if (!emailRegex.test(groupEmail)) {
              setHasError(true);
            } else {
              setHasError(false);
              groupMutate();
            }
          }
        }}
        mutateLoading={isPending || isGroupPending}
      />
    </Stack>
  );
}
